/* eslint-disable no-unused-vars */
import React, { useEffect, useState } from "react";
// ANT DESIGN COMPONENTS
import {
	Drawer,
	Button,
	Divider,
	Switch,
	TimePicker,
	Select,
	DatePicker,
	Tooltip,
	Input,
} from "antd";
// DEVICE DETECT
import { isMobile } from "react-device-detect";
// I18N TRANSLATION
import { useTranslation } from "react-i18next";
// MOMENT DATE FORMAT
import moment from "moment";
// FONT AWESOME LIBRYARY AND ICONS
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
	faAdd,
	faBan,
	faCalendar,
	faFilter,
	faGlobe,
	faTrash,
} from "@fortawesome/free-solid-svg-icons";
// REDUX
import { useSelector, useDispatch } from "react-redux";
import {
	updateOpenDrawerFilters,
	updateReportRefreshTable,
	updateFilterExclusionSaturdays,
	updateFilterExclusionSundays,
	updateFilterExclusionHolidays,
	updateFilterLiftType,
	updateFilterExclusionTimeRange,
	updateFilterAddExclusionTimeRanges,
	updateFilterExclusionDateRange,
	updateFilterAddExclusionDateRanges,
	updateFilterType,
	updateFilterUseCustomSearch,
} from "../../redux/reports/reportsSlice";
import { useLazyGetHolidaysQuery } from "../../redux/organizations/organizationAPI";
// COMPONENTS
import ReportsFilterStartDate from "./ReportsFilterStartDate";
import ReportsFilterEndDate from "./ReportsFilterEndDate";
import ReportsFilterOffsetType from "./ReportsFilterOffsetType";
import ReportsFilterTimestamp from "./ReportsFilterTimestamp";
import ReportsFilterSelectColumns from "./ReportsFilterSelectColumns";
import ReportsFilterGetBanks from "./ReportsFilterGetBanks";
import ReportsFilterGetServers from "./ReportsFilterGetServers";
import ReportsFilterGetOrganizations from "./ReportsFilterGetOrganizations";
import ReportsFilterGetLifts from "./ReportsFilterGetLifts";
// INTERFACES
import { RootState } from "../../app/store";
import { ReportsFilterDrawerInterface } from "../../interfaces/Reports.interface";
import { HolidayInterface } from "../../interfaces/Organizations.interface";
// AUTHORIZATION
import GETJwtToken from "../../redux/authentication/authentication";

function ReportsFilterDrawer({
	level,
	filterManagement,
	disabledLevelSelect,
	defaultOffsets,
	getDataWhenOpen,
	showDatePicker,
	showTypeSelect,
}: ReportsFilterDrawerInterface) {
	// ************************************************ */
	// GLOBAL VARIABLES ******************************* */
	const dispatch = useDispatch();
	const [t] = useTranslation("global");
	const { Option } = Select;
	const { RangePicker } = DatePicker;

	// ************************************************ */
	// USE STATE VARIABLES **************************** */
	const [exclusionRangeDate, setExclusionRangeDate] = useState<any>([
		"2024/01/01 00:00",
		"2024/01/01 23:59",
	]);
	const [exclusionRangeTime, setExclusionRangeTime] = useState<any>([
		"00:00",
		"23:59",
	]);
	const [holidays, setHolidays] = useState<HolidayInterface[]>([]);

	// ************************************************ */
	// REDUX SLICE VARIABLES ************************** */
	const {
		openDrawerFilters: openDrawer,
		filterExclusionSaturdays,
		filterExclusionSundays,
		filterExclusionHolidays,
		filterLiftType,
		filterAddExclusionTimeRanges,
		filterExclusionTimeRange,
		filterCascaderTypeLevel,
		filterCascaderTypeArr,
		filterStartDate,
		filterEndDate,
		filterCascaderValue,
		filterExclusionDateRange,
		filterAddExclusionDateRanges,
		filterIdOrgSelected,
		filterUseCustomSearch,
		filterType,
	} = useSelector((state: RootState) => state.report);
	const { theme } = useSelector((state: RootState) => state.home);

	// ************************************************ */
	// SERVICES AND API CALLS ************************* */
	const [triggerGetHolidays, { data: dataHolidays }] =
		useLazyGetHolidaysQuery();

	// ************************************************ */
	// FUNCTIONS ************************************** */
	const onClickClose = () => {
		dispatch(updateOpenDrawerFilters(false));
	};

	const onChangeExclusionSaturdays = (newValue: boolean) => {
		dispatch(updateFilterExclusionSaturdays(newValue));
	};

	const onChangeExclusionSundays = (newValue: boolean) => {
		dispatch(updateFilterExclusionSundays(newValue));
	};

	const onChangeExclusionHolidays = (newValue: boolean) => {
		dispatch(updateFilterExclusionHolidays(newValue));
	};

	const onChangeLiftType = (newValue: string) => {
		dispatch(updateFilterLiftType(newValue));
	};

	const onChangeAddExclusionTimeRanges = (newValue: boolean) => {
		dispatch(updateFilterAddExclusionTimeRanges(newValue));
	};

	const onChangeAddExclusionDateRanges = (newValue: boolean) => {
		dispatch(updateFilterAddExclusionDateRanges(newValue));
	};

	const onClickSearch = () => {
		onClickClose();
		dispatch(updateReportRefreshTable(true));
		setTimeout(() => {
			dispatch(updateReportRefreshTable(false));
		}, 1000);
	};

	const disableSearchAndRefreshButton = () => {
		let disableButton = false;
		if (
			(!filterUseCustomSearch.useCustomSearch
				? filterCascaderTypeLevel === 0 ||
				  filterCascaderTypeArr.length === 0 ||
				  filterCascaderValue.length === 0
				: filterUseCustomSearch.value === "" ||
				  filterUseCustomSearch.type === "") ||
			filterStartDate === "" ||
			filterEndDate === ""
		) {
			disableButton = true;
		}
		return disableButton;
	};

	const onChangeExclusionTimeRange = (
		value: any,
		dateString: [string, string] | string
	) => {
		setExclusionRangeTime(dateString);
	};

	const onChangeExclusionDateRange = (
		value: any,
		dateString: [string, string] | string
	) => {
		setExclusionRangeDate(dateString);
	};

	const onClickAddNewExclusionDateRange = () => {
		const copy = structuredClone(filterExclusionDateRange);
		copy.push(exclusionRangeDate);
		dispatch(updateFilterExclusionDateRange(copy));
		setExclusionRangeDate(["2024/01/01 00:00", "2024/01/01 23:59"]);
	};

	const onClickRemoveExclusionDateRange = (index: number) => {
		const copy = structuredClone(filterExclusionDateRange);
		copy.splice(index, 1);
		dispatch(updateFilterExclusionDateRange(copy));
	};

	const onClickAddNewExclusionTimeRange = () => {
		const copy = structuredClone(filterExclusionTimeRange);
		copy.push(exclusionRangeTime);
		dispatch(updateFilterExclusionTimeRange(copy));
		setExclusionRangeTime(["00:00", "23:59"]);
	};

	const onClickRemoveExclusionTimeRange = (index: number) => {
		const copy = structuredClone(filterExclusionTimeRange);
		copy.splice(index, 1);
		dispatch(updateFilterExclusionTimeRange(copy));
	};

	const getHolidaysByOrganization = async (orgId: string) => {
		const token = await GETJwtToken();
		triggerGetHolidays({
			token,
			orgId,
		});
	};

	const onChangeFilterType = (newValue: string) => {
		dispatch(updateFilterType(newValue));
		const copy = structuredClone(filterUseCustomSearch);
		copy.type = newValue;
		copy.value = "";
		dispatch(updateFilterUseCustomSearch(copy));
	};
	// ************************************************ */
	// USE EFFECT ************************************* */
	useEffect(() => {
		if (dataHolidays && dataHolidays.data && dataHolidays.data.length > 0) {
			setHolidays(dataHolidays.data);
		} else {
			setHolidays([]);
		}
	}, [dataHolidays]);

	useEffect(() => {
		if (filterUseCustomSearch.orgId) {
			getHolidaysByOrganization(filterUseCustomSearch.orgId);
		}
	}, [filterUseCustomSearch]);
	// ************************************************ */
	// COMPONENT ************************************** */
	return (
		<Drawer
			width='35%'
			placement='right'
			onClose={onClickClose}
			closable={false}
			open={openDrawer}
		>
			{/** ************************************************************************ */}
			{/** ADVANCE FILTERS */}
			{filterManagement.advanceFilters && (
				<>
					<div>
						<Divider
							orientation='left'
							className='generalStyles__drawerDivider'
						>
							<h5>
								<FontAwesomeIcon
									icon={faFilter}
									className='generalStyles__info generalStyles__mrFix'
								/>
								{t("general.advanceFilters")}
							</h5>
						</Divider>
					</div>
					<div className={`drawer__box__${theme}`}>
						{showDatePicker && (
							<>
								{/* START DATE */}
								<div className='generalStyles__flex'>
									<div>
										<p
											className={
												isMobile ? "drawer__titleMobile" : "drawer__title"
											}
											style={{ paddingTop: "10px" }}
										>
											<span className='generalStyles__inputFlexRequired'>
												*
											</span>
											{t("general.startDate")}:
										</p>
									</div>
									<div className='drawer__textContainer generalStyles__width100'>
										<ReportsFilterStartDate showDatePicker={showDatePicker} />
									</div>
								</div>
								{/* END DATE */}
								<div className='generalStyles__flex mt-2'>
									<div>
										<p
											className={
												isMobile ? "drawer__titleMobile" : "drawer__title"
											}
											style={{ paddingTop: "10px" }}
										>
											{" "}
											<span className='generalStyles__inputFlexRequired'>
												*
											</span>
											{t("general.endDate")}:
										</p>
									</div>
									<div className='drawer__textContainer generalStyles__width100'>
										<ReportsFilterEndDate showDatePicker={showDatePicker} />
									</div>
								</div>
							</>
						)}
						{/* SELECT */}
						{filterUseCustomSearch.showSelect && (
							<div className='generalStyles__flex mt-2'>
								<div>
									<p
										className={
											isMobile ? "drawer__titleMobile" : "drawer__title"
										}
										style={{ paddingTop: "10px" }}
									>
										{" "}
										<span className='generalStyles__inputFlexRequired'>*</span>
										Filter Type:
									</p>
								</div>
								<div className='drawer__textContainer generalStyles__width100'>
									<Select
										showSearch
										optionFilterProp='children'
										className='generalStyles__width100'
										allowClear
										value={filterType}
										onChange={onChangeFilterType}
									>
										{["Organizations", "Servers", "Banks", "Lifts"].map(
											(item: string) => (
												<Option key={item} value={item}>
													{item}
												</Option>
											)
										)}
									</Select>
								</div>
							</div>
						)}
						{/** SELECT BY FILTER */}
						<div className='generalStyles__flex mt-2'>
							<div>
								<p
									className={isMobile ? "drawer__titleMobile" : "drawer__title"}
									style={{ paddingTop: "10px" }}
								>
									{" "}
									<span className='generalStyles__inputFlexRequired'>*</span>
									{filterType}:
								</p>
							</div>
							<div className='drawer__textContainer generalStyles__width100'>
								{filterType === "Servers" && (
									<ReportsFilterGetServers getDataWhenOpen={getDataWhenOpen} />
								)}
								{filterType === "Banks" && (
									<ReportsFilterGetBanks getDataWhenOpen={getDataWhenOpen} />
								)}
								{filterType === "Organizations" && (
									<ReportsFilterGetOrganizations
										getDataWhenOpen={getDataWhenOpen}
									/>
								)}
								{filterType === "Lifts" && (
									<ReportsFilterGetLifts getDataWhenOpen={getDataWhenOpen} />
								)}
							</div>
						</div>

						{/* LIFT TYPE */}
						{showTypeSelect && (
							<div className='generalStyles__flex mt-2'>
								<div>
									<p
										className={
											isMobile ? "drawer__titleMobile" : "drawer__title"
										}
										style={{ paddingTop: "10px" }}
									>
										<span className='generalStyles__inputFlexRequired'>*</span>
										{t("edgeManagement.lifts.type")}
									</p>
								</div>
								<div className='drawer__textContainer generalStyles__width100'>
									<Select
										showSearch
										optionFilterProp='children'
										className='generalStyles__width100'
										allowClear
										value={filterLiftType}
										onChange={onChangeLiftType}
									>
										{["ALL", "ELEVATOR", "ESCALATOR", "MOVINGWALK"].map(
											(item: string) => (
												<Option key={item} value={item}>
													{item}
												</Option>
											)
										)}
									</Select>{" "}
								</div>
							</div>
						)}
					</div>
				</>
			)}
			<div>
				<br />
				<Divider orientation='left' className='generalStyles__drawerDivider'>
					<h5>
						<FontAwesomeIcon
							icon={faGlobe}
							className='generalStyles__info generalStyles__mrFix'
						/>
						{t("general.timezone")}
					</h5>
				</Divider>
			</div>
			<div className={`drawer__box__${theme}`}>
				{/* TIMEZONE LIST */}
				<ReportsFilterTimestamp />
			</div>
			{/** ************************************************************************ */}
			{/** EXCLUSION FILTERS */}
			{filterManagement.exclusions && (
				<>
					<br />
					<div>
						<Divider
							orientation='left'
							className='generalStyles__drawerDivider'
						>
							<h5>
								<FontAwesomeIcon
									icon={faBan}
									className='generalStyles__info generalStyles__mrFix'
								/>
								{t("general.exclusions")}
							</h5>
						</Divider>
					</div>
					<div className={`drawer__box__${theme}`}>
						{/* EXCLUDE SATURDAYS */}
						<div className='generalStyles__flex'>
							<div>
								<p
									className={isMobile ? "drawer__titleMobile" : "drawer__title"}
									style={{ paddingTop: "5px" }}
								>
									{t("general.saturdays")}
								</p>
							</div>
							<div className='drawer__textContainer generalStyles__width100'>
								<Switch
									onChange={onChangeExclusionSaturdays}
									checked={filterExclusionSaturdays}
								/>
							</div>
						</div>
						{/* EXCLUDE SUNDAYS */}
						<div className='generalStyles__flex mt-2'>
							<div>
								<p
									className={isMobile ? "drawer__titleMobile" : "drawer__title"}
									style={{ paddingTop: "5px" }}
								>
									{t("general.sundays")}
								</p>
							</div>
							<div className='drawer__textContainer generalStyles__width100'>
								<Switch
									onChange={onChangeExclusionSundays}
									checked={filterExclusionSundays}
								/>
							</div>
						</div>
						{/* EXCLUDE HOLIDAYS */}
						<div className='generalStyles__flex mt-2'>
							<div>
								<p
									className={isMobile ? "drawer__titleMobile" : "drawer__title"}
									style={{ paddingTop: "5px" }}
								>
									{t("general.holidays")}
								</p>
							</div>
							<div className='drawer__textContainer generalStyles__width100'>
								<Tooltip
									title={
										holidays.length === 0
											? "The selected organization has no holidays assigned."
											: ""
									}
								>
									<Switch
										onChange={onChangeExclusionHolidays}
										checked={filterExclusionHolidays}
										disabled={
											holidays.length === 0 ||
											filterUseCustomSearch.value === ""
										}
									/>
								</Tooltip>
							</div>
						</div>
						{filterExclusionHolidays && (
							<>
								{holidays.map((holiday: HolidayInterface) => (
									<div className='generalStyles__flex drawer__container'>
										<div>
											<p
												className={
													isMobile ? "drawer__titleMobile" : "drawer__title"
												}
											/>
										</div>
										<div className='drawer__titleMobile generalStyles__width100 mt-1'>
											<Tooltip
												title={`${
													holiday && holiday.month
														? moment()
																.month(holiday.month - 1)
																.format("MMMM")
														: "--"
												} - ${holiday && holiday.day ? holiday.day : "--"} / ${
													holiday && holiday.name ? holiday.name : "--"
												}`}
											>
												<Input
													prefix={
														<FontAwesomeIcon
															icon={faCalendar}
															style={{
																marginTop: "-5px",
																marginLeft: "-5px",
																marginRight: "5px",
															}}
														/>
													}
													disabled
													value={`${
														holiday && holiday.month
															? moment()
																	.month(holiday.month - 1)
																	.format("MMMM")
															: "--"
													} - ${
														holiday && holiday.day ? holiday.day : "--"
													} / ${holiday && holiday.name ? holiday.name : "--"}`}
												/>
											</Tooltip>
										</div>
									</div>
								))}
							</>
						)}
						{/* EXCLUSION TIME */}
						<div className='generalStyles__flex mt-2'>
							<div>
								<p
									className={isMobile ? "drawer__titleMobile" : "drawer__title"}
									style={{ paddingTop: "5px" }}
								>
									{t("general.exclusionTime")}
								</p>
							</div>
							<div className='drawer__textContainer generalStyles__width100'>
								<Switch
									onChange={(e) => {
										onChangeAddExclusionTimeRanges(e);
									}}
									checked={filterAddExclusionTimeRanges}
								/>
							</div>
						</div>
						{filterAddExclusionTimeRanges && (
							<>
								<div className='generalStyles__flex mt-2'>
									<div>
										<p
											className={
												isMobile ? "drawer__titleMobile" : "drawer__title"
											}
											style={{ paddingTop: "10px" }}
										/>
									</div>
									<div className='drawer__textContainer generalStyles__width100'>
										<div className='generalStyles__flex'>
											<TimePicker.RangePicker
												format='HH:mm'
												className='generalStyles__width100'
												onChange={onChangeExclusionTimeRange}
												value={[
													moment(exclusionRangeTime[0], "HH:mm"),
													moment(exclusionRangeTime[1], "HH:mm"),
												]}
												allowClear={false}
												disabled={!filterAddExclusionTimeRanges}
											/>
											<Tooltip title='Add new exclusion time range'>
												<Button
													onClick={onClickAddNewExclusionTimeRange}
													type='primary'
													shape='circle'
													className='generalStyles__mlFix'
													icon={<FontAwesomeIcon icon={faAdd} />}
												/>
											</Tooltip>
										</div>
									</div>
								</div>
								<div className='generalStyles__flex'>
									<div>
										<p
											className={
												isMobile ? "drawer__titleMobile" : "drawer__title"
											}
											style={{ paddingTop: "5px" }}
										/>
									</div>
									<div className='drawer__textContainer generalStyles__width100'>
										{filterExclusionTimeRange.map(
											(item: any, index: number) => (
												<div className='generalStyles__flex mt-3' key={index}>
													<TimePicker.RangePicker
														format='HH:mm'
														className='generalStyles__width100'
														value={[
															moment(item[0], "HH:mm"),
															moment(item[1], "HH:mm"),
														]}
														allowClear={false}
														disabled
													/>
													<Tooltip title={t("general.delete")}>
														<Button
															onClick={() => {
																onClickRemoveExclusionTimeRange(index);
															}}
															shape='circle'
															className='generalStyles__mlFix buttonStyle__4'
															icon={<FontAwesomeIcon icon={faTrash} />}
														/>
													</Tooltip>
												</div>
											)
										)}
									</div>
								</div>
							</>
						)}
						{/* EXCLUSION DATE RANGE */}
						<div className='generalStyles__flex mt-2'>
							<div>
								<p
									className={isMobile ? "drawer__titleMobile" : "drawer__title"}
									style={{ paddingTop: "5px" }}
								>
									{t("general.dateRanges")}
								</p>
							</div>
							<div className='drawer__textContainer generalStyles__width100'>
								<Switch
									onChange={(e) => {
										onChangeAddExclusionDateRanges(e);
									}}
									checked={filterAddExclusionDateRanges}
								/>
							</div>
						</div>
						{filterAddExclusionDateRanges && (
							<>
								<div className='generalStyles__flex mt-2'>
									<div>
										<p
											className={
												isMobile ? "drawer__titleMobile" : "drawer__title"
											}
											style={{ paddingTop: "5px" }}
										/>
									</div>
									<div className='drawer__textContainer generalStyles__width100'>
										<div className='generalStyles__flex'>
											<RangePicker
												format='YYYY-MM-DD HH:mm'
												allowClear={false}
												className='generalStyles__width100'
												showTime
												value={[
													moment(exclusionRangeDate[0], "YYYY-MM-DD HH:mm"),
													moment(exclusionRangeDate[1], "YYYY-MM-DD HH:mm"),
												]}
												onChange={onChangeExclusionDateRange}
											/>
											<Tooltip title='Add new exclusion date range'>
												<Button
													onClick={onClickAddNewExclusionDateRange}
													type='primary'
													shape='circle'
													className='generalStyles__mlFix'
													icon={<FontAwesomeIcon icon={faAdd} />}
												/>
											</Tooltip>
										</div>
									</div>
								</div>
								<div className='generalStyles__flex'>
									<div>
										<p
											className={
												isMobile ? "drawer__titleMobile" : "drawer__title"
											}
											style={{ paddingTop: "5px" }}
										/>
									</div>
									<div className='drawer__textContainer generalStyles__width100'>
										{filterExclusionDateRange.map(
											(item: any, index: number) => (
												<div className='generalStyles__flex mt-3' key={index}>
													<RangePicker
														format='YYYY-MM-DD HH:mm'
														allowClear={false}
														className='generalStyles__width100'
														showTime
														value={[
															moment(item[0], "YYYY-MM-DD HH:mm"),
															moment(item[1], "YYYY-MM-DD HH:mm"),
														]}
														disabled
													/>
													<Tooltip title={t("general.delete")}>
														<Button
															onClick={() => {
																onClickRemoveExclusionDateRange(index);
															}}
															shape='circle'
															className='generalStyles__mlFix buttonStyle__4'
															icon={<FontAwesomeIcon icon={faTrash} />}
														/>
													</Tooltip>
												</div>
											)
										)}
									</div>
								</div>
							</>
						)}
					</div>
				</>
			)}
			{/** ************************************************************************ */}
			{/** OFFSETS FILTERS */}
			<br />
			{filterManagement.showOffsets && (
				<ReportsFilterOffsetType
					defaultOffsets={defaultOffsets}
					disableOffsets={filterManagement.offsets}
				/>
			)}
			{/** ************************************************************************ */}
			{/** OFFSETS FILTERS */}
			<ReportsFilterSelectColumns />
			{/** ************************************************************************ */}
			{/** CLOSE AND SUBMIT BUTTONS */}
			<div className='mt-4'>
				<div>
					<Button
						type='default'
						onClick={onClickClose}
						className='buttonStyle__3'
					>
						{t("edgeManagement.close")}
					</Button>
					<Button
						onClick={onClickSearch}
						type='primary'
						className='buttonStyle__3'
						disabled={disableSearchAndRefreshButton()}
					>
						{t("general.search")}
					</Button>
					<br />
				</div>
			</div>
		</Drawer>
	);
}

export default ReportsFilterDrawer;
